import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Bell, ChevronDown, User, LogOut } from "lucide-react";
import { PortalService } from "services/portal";

const ROUTE_TITLES = {
  "/portal/dashboard": "Dashboard",
  "/portal/compras": "Registro de Compras",
  "/portal/ventas": "Registro de Ventas",
  "/portal/documentos": "Documentos",
  "/portal/notificaciones": "Notificaciones",
  "/portal/perfil": "Mi Perfil",
};

function getInitial(name = "") {
  const t = String(name).trim();
  if (!t) return "?";
  return t.charAt(0).toUpperCase();
}

function readPortalUser() {
  try {
    const data = JSON.parse(localStorage.getItem("userdata:portal") || "null");
    if (!data?.token) return null;
    const payload = JSON.parse(atob(data.token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return { email: payload.email };
  } catch {
    return null;
  }
}

export function PortalHeader({ onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const user = readPortalUser();

  const title = ROUTE_TITLES[location.pathname] || "Portal";

  useEffect(() => {
    let cancelled = false;
    PortalService.getUnreadCount()
      .then((res) => { if (!cancelled) setUnread(res?.count ?? res?.unread ?? 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 32px",
        background: "var(--bg-card)",
        borderBottom: "1px solid var(--border)",
        position: "sticky",
        top: 0,
        zIndex: 10,
        transition: "background-color 200ms, border-color 200ms",
      }}
    >
      <h2 style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)", margin: 0 }}>{title}</h2>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Link
          to="/portal/notificaciones"
          aria-label="Notificaciones"
          style={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 36,
            height: 36,
            borderRadius: 8,
            color: "var(--text-secondary)",
            textDecoration: "none",
            transition: "background 120ms",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <Bell size={18} />
          {unread > 0 && (
            <span
              style={{
                position: "absolute",
                top: 6,
                right: 6,
                minWidth: 16,
                height: 16,
                padding: "0 4px",
                borderRadius: 999,
                background: "var(--negative)",
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Link>

        <div ref={menuRef} style={{ position: "relative" }}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 8px 4px 4px",
              background: "transparent",
              border: "1px solid transparent",
              borderRadius: 8,
              cursor: "pointer",
              color: "var(--text-primary)",
              transition: "background 120ms",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "var(--accent)",
                color: "var(--accent-fg)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {getInitial(user?.email)}
            </span>
            <ChevronDown size={14} color="var(--text-secondary)" />
          </button>

          {menuOpen && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                right: 0,
                marginTop: 6,
                minWidth: 200,
                background: "var(--bg-card)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                boxShadow: "var(--shadow-lg)",
                zIndex: 20,
                overflow: "hidden",
              }}
            >
              {user?.email && (
                <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--border-light)", fontSize: 12, color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {user.email}
                </div>
              )}
              <button
                onClick={() => { setMenuOpen(false); navigate("/portal/perfil"); }}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "transparent", border: "none", textAlign: "left", cursor: "pointer", fontSize: 13, color: "var(--text-secondary)" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-elevated)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <User size={14} /> Mi perfil
              </button>
              <button
                onClick={() => { setMenuOpen(false); onLogout && onLogout(); }}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "transparent", border: "none", textAlign: "left", cursor: "pointer", fontSize: 13, color: "var(--negative)" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--negative-bg)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <LogOut size={14} /> Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
